/**
 * The cloud's own refusal of a **tunnel session** (`POST /api/webrtc/session`),
 * as a value.
 *
 * The third member of this app's connect-failure vocabulary, beside
 * `JoinFailureCode` (`services/hubJoin.ts` — the hub/cloud join) and
 * `LocalRouteRefusal` (`services/hubLocalFirst.ts` — the LAN route): **named
 * refusals, never a boolean**, because each value is a different fact and a
 * surface words each one differently.
 *
 * The first value is the product's first **enforced entitlement** (plan
 * `docs/plans/FLOOR-PLAN-AND-PLAN-EDITOR.md` §3.2, owner decision D3): a hub whose
 * plan terms are local-only cannot be reached through the cloud tunnel, and the
 * cloud refuses the session with **403** and `code: 'remote_access_not_licensed'`.
 * That is not "the connection failed" — the session is refused on purpose, and the
 * person is told what it means: the sentence lives in the six catalogues under
 * `hub.open.failure.*` (this module names the fact, `utils/hubOpenFallback.ts`
 * names the sentence).
 *
 * Pure: no store, no transport, no `import.meta` — the matrix is a unit test.
 */

/** Every way the cloud's session route can refuse *by name*. */
export const CLOUD_SESSION_REFUSALS = [
  /** The hub's plan terms are local-only: the tunnel is not a route to it. */
  'remote_access_not_licensed',
] as const;

export type CloudSessionRefusal = (typeof CLOUD_SESSION_REFUSALS)[number];

/**
 * The server's own `code` → this client's vocabulary.
 *
 * A `Record`, like `hubJoin.ts`'s own code table: a code the cloud starts sending
 * without a line here is a build error the moment the type is extended, so a
 * refusal cannot reach a surface as a bare string.
 */
const SERVER_CODES: Record<string, CloudSessionRefusal> = {
  remote_access_not_licensed: 'remote_access_not_licensed',
};

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * A refused session → the refusal this client can **name**, or `null`.
 *
 * Only the server's own `code` names one — never the status. `403` is
 * `remote_access_not_licensed` for a hub that is local-only and "Access denied to
 * this hub" for a hub the caller has no role on; the two must not be worded the
 * same, and the route checks authorization first on purpose (card `t_61606665`).
 * A `null` leaves the failure to the caller's own line.
 *
 * `status` is a guard, not a source: an answer that is not a refusal cannot be
 * one, whatever its body says.
 */
export function classifyCloudSessionRefusal(status: number | null, body: unknown): CloudSessionRefusal | null {
  if (status !== null && status < 400) return null;
  const raw = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const code = stringOrNull(raw?.code);
  if (!code) return null;
  return SERVER_CODES[code] ?? null;
}
